import { useState } from "react";
import { TbBrandTelegram } from "react-icons/tb";
import { joinWaitlist } from "../lib/api";
import { currentSource } from "../lib/attribution";
import { analytics } from "../lib/analytics";
import { TELEGRAM_GROUP } from "../lib/faData";

/**
 * The Farsi email capture, extracted from the home route on 22 Aug 2026 so the
 * article pages can carry it too.
 *
 * Why this component exists at all: until today the form lived inline in
 * `routes/home.tsx` and NOWHERE else, so `fa.gazar.dev/` had one `type="email"`
 * input and `fa.gazar.dev/maghale/<slug>` had zero. The only two mentions of
 * خبرنامه on an article page were navigation links pointing back to
 * `/#newsletter`, meaning a reader who arrived on an article, read it, and was
 * persuaded had to navigate away and hunt for a section to act on it.
 *
 * That matters more than it looks. Arrivals tagged `?src=mk` land on `/`, which
 * has always had the form, so the Maktabkhooneh number was never affected. But
 * every Telegram and every Virgool arrival lands on an ARTICLE, and those are
 * the only two rails that reach anyone who is not already a student. The 27 Nov
 * gate pre-committed to reading arrivals and addresses separately, and wrote
 * down that "over 100 arrivals with under 10 addresses is not a kill, it is a
 * copy problem". This was that copy problem, shipped and live.
 *
 * `variant` exists so the article page is not a second implementation that can
 * drift from the home page's. One form, one submit path, one analytics event.
 */
type Variant = "section" | "article";

export default function NewsletterSignup({ variant = "section" }: { variant?: Variant }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const src = currentSource();
      await joinWaitlist(email.trim(), src);
      // `where` mirrors telegramClick's: it lets November tell an address
      // captured off an article from one captured off the landing page, which
      // are different signals about different rails.
      analytics.signup({ src, where: variant === "article" ? "article" : "home" });
      setStatus("done");
      setEmail("");
    } catch (err) {
      // The API's message is English. Inside an otherwise Persian page that is
      // worse than a generic Persian line, so it goes to the console and the
      // reader gets something they can act on.
      console.error(err);
      setStatus("error");
    }
  }

  const isArticle = variant === "article";

  const form =
    status === "done" ? (
      <p
        role="status"
        className="rounded-smooth-lg border border-brand-forest/30 bg-brand-cream px-6 py-5 text-[17px] leading-[2] text-brand-forest"
      >
        ثبت شد. ممنون که پیوستید.
      </p>
    ) : (
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
        {/* The id has to be unique per page. Only one of these renders on any
            given route today, but a duplicated id is the kind of thing that
            silently breaks a label association rather than throwing. */}
        <label htmlFor={isArticle ? "fa-email-article" : "fa-email"} className="sr-only">
          نشانی ایمیل
        </label>
        <input
          id={isArticle ? "fa-email-article" : "fa-email"}
          type="email"
          required
          dir="ltr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="flex-1 rounded-smooth border border-border-field bg-white px-4 py-3 text-[16px] text-start placeholder:text-gray-900/35 focus:border-brand-navy focus:outline-none"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="rounded-smooth bg-brand-navy px-8 py-3 text-[16px] font-bold text-white transition-opacity duration-150 hover:opacity-90 disabled:opacity-60"
        >
          {status === "loading" ? "در حال ثبت…" : "عضویت"}
        </button>
      </form>
    );

  const errorLine = status === "error" && (
    <p role="alert" className="mt-4 text-[15px] leading-[2] text-brand-red">
      ثبت نشد. لطفاً یک بار دیگر تلاش کنید.
    </p>
  );

  /* The Telegram group, deliberately BELOW the email form and visually quieter
     than it.

     Both capture the same visitor and only one of them is owned: an address
     survives a platform, a group membership does not, and the 27 Nov gate
     counts addresses. Putting the group first would grow a number nobody is
     measured on at the expense of the one they are. So it is offered second, as
     the lower-commitment option for somebody who was never going to hand over
     an email at all. */
  const telegram = (
    <div className="mt-10 border-t border-border-subtle pt-8">
      <p className="mb-4 text-[16px] leading-[2.1] text-gray-900/70">
        {status === "done"
          ? "گفت‌وگوها هم در گروه تلگرام جریان دارد. اگر دوست داشتید، آنجا هم به ما بپیوندید."
          : "ترجیح می‌دهید ایمیل ندهید؟ گفت‌وگوها در گروه تلگرام جریان دارد؛ نوشته‌های تازه هم همان‌جا اعلام می‌شود."}
      </p>
      <a
        href={TELEGRAM_GROUP}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => analytics.telegramClick({ where: "cta", src: currentSource() })}
        className="inline-flex items-center gap-2 rounded-smooth border border-border-field bg-white px-6 py-3 text-[16px] font-bold text-brand-navy transition-colors duration-150 hover:border-brand-navy"
      >
        <TbBrandTelegram size={20} aria-hidden="true" />
        پیوستن به گروه تلگرام
      </a>
    </div>
  );

  if (isArticle) {
    /* Quieter than the home section on purpose: it follows an article rather
       than closing a landing page, and the reader has already been given
       something before being asked for anything. */
    return (
      <section
        aria-labelledby="fa-newsletter-article"
        className="mt-14 rounded-smooth-lg border border-border-subtle bg-brand-cream/40 px-6 py-8 sm:px-8"
      >
        <h2
          id="fa-newsletter-article"
          className="mb-3 text-[22px] font-bold leading-[1.7] text-brand-navy md:text-[24px]"
        >
          خبرنامه‌ی فارسی
        </h2>
        <p className="mb-6 text-[16px] leading-[2.1] text-gray-900/70">
          اگر این نوشته به کارتان آمد، نوشته‌ی بعدی را مستقیم برایتان می‌فرستم. نه تبلیغ، نه اسپم، و
          لغو اشتراک با یک کلیک.
        </p>
        {form}
        {errorLine}
        <div className="mt-8 border-t border-border-subtle pt-6">
          <p className="mb-4 text-[15px] leading-[2.1] text-gray-900/70">
            {status === "done"
              ? "گفت‌وگوها هم در گروه تلگرام جریان دارد."
              : "ترجیح می‌دهید ایمیل ندهید؟ نوشته‌های تازه در گروه تلگرام هم اعلام می‌شود."}
          </p>
          <a
            href={TELEGRAM_GROUP}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => analytics.telegramClick({ where: "cta", src: currentSource() })}
            className="inline-flex items-center gap-2 rounded-smooth border border-border-field bg-white px-5 py-2.5 text-[15px] font-bold text-brand-navy transition-colors duration-150 hover:border-brand-navy"
          >
            <TbBrandTelegram size={18} aria-hidden="true" />
            پیوستن به گروه تلگرام
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="newsletter" className="bg-white py-16 md:py-20 scroll-mt-4">
      <div className="max-w-2xl mx-auto px-4 text-center sm:px-6 lg:px-8">
        <h2 className="mb-4 text-[28px] font-bold leading-[1.6] text-brand-navy md:text-[34px]">
          خبرنامه‌ی فارسی
        </h2>
        <p className="mb-8 text-[17px] leading-[2.1] text-gray-900/70">
          هر زمان که مقاله یا دوره‌ی فارسی جدیدی منتشر کنم، شما اولین نفری خواهید بود که مطلع
          می‌شوید. از من ایمیل‌های تبلیغاتی یا اسپم دریافت نخواهید کرد و هر زمان که مایل بودید، تنها
          با یک کلیک می‌توانید اشتراک خود را لغو کنید.
        </p>
        {form}
        {errorLine}
        {telegram}
      </div>
    </section>
  );
}
